import { Box, Flex, Text } from '@chakra-ui/react';
import { Card, CardHeader, CardBody } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Skeleton } from '@/components/ui/Skeleton';
import type { StrategistOutput, RiskDriver } from '@market-buddy/shared';

interface TradeOffLensProps {
  riskScore: number | null;
  riskBand: string | null;
  opportunityScore: number | null;
  riskDrivers: RiskDriver[];
  strategist: StrategistOutput | null;
  strategistLoading: boolean;
  strategistError: string | null;
}

export function TradeOffLens({
  riskScore,
  riskBand,
  opportunityScore,
  riskDrivers,
  strategist,
  strategistLoading,
  strategistError,
}: TradeOffLensProps) {
  if (riskScore === null) return null;

  return (
    <Card>
      <CardHeader>
        <Text fontSize="18px" fontWeight="500" lineHeight="28px" color="text.primary">Trade-off Lens</Text>
      </CardHeader>
      <CardBody>
        <Flex direction="column" gap="4">
          {/* Risk bar */}
          <ScoreBar
            label="Risk"
            score={riskScore}
            maxScore={100}
            color={riskBarColor(riskBand)}
            sublabel={riskBand ? `${riskBand} risk` : undefined}
            authoritative
          />

          {/* Opportunity bar */}
          {opportunityScore !== null && (
            <ScoreBar
              label="Opportunity"
              score={opportunityScore}
              maxScore={100}
              color="var(--chakra-colors-brand-500, #2970ff)"
              authoritative
            />
          )}

          {/* Risk drivers */}
          {riskDrivers.length > 0 && (
            <Box mt="1">
              <Text fontSize="12px" fontWeight="500" lineHeight="18px" color="text.tertiary" mb="2">
                Top risk factors
              </Text>
              {riskDrivers.map((d) => (
                <Flex
                  key={d.key}
                  justify="space-between"
                  align="baseline"
                  py="2px"
                >
                  <Text fontSize="14px" color="text.secondary">{d.label}</Text>
                  <Text fontFamily="mono" fontSize="12px" css={{ fontVariantNumeric: 'tabular-nums' }} color="text.tertiary">
                    {(d.contribution * 100).toFixed(0)}%
                  </Text>
                </Flex>
              ))}
            </Box>
          )}

          {/* Strategist commentary (non-authoritative) */}
          {(strategist || strategistLoading) && (
            <Box
              borderTop="1px dashed"
              borderColor="border.secondary"
              pt="3"
              mt="1"
            >
              <Flex align="center" gap="2" mb="2">
                <Text fontSize="12px" fontWeight="500" lineHeight="18px" color="text.tertiary" fontStyle="italic">
                  AI Commentary
                </Text>
                <Badge variant="neutral">non-binding</Badge>
              </Flex>

              {strategistLoading ? (
                <Flex direction="column" gap="2">
                  {Array.from({ length: 3 }, (_, i) => (
                    <Skeleton key={i} height={16} />
                  ))}
                </Flex>
              ) : strategist ? (
                <Flex direction="column" gap="3">
                  {/* Conviction score */}
                  <Flex justify="space-between" align="center">
                    <Text fontSize="12px" fontWeight="500" lineHeight="18px" color="text.tertiary">
                      Conviction (non-binding estimate)
                    </Text>
                    <Text fontFamily="mono" fontSize="14px" css={{ fontVariantNumeric: 'tabular-nums' }} color="text.secondary">
                      {strategist.conviction_score}/100
                    </Text>
                  </Flex>

                  {/* Why risky */}
                  {strategist.why_risky.length > 0 && (
                    <BulletSection title="Why risky" items={strategist.why_risky} variant="warning" />
                  )}

                  {/* Why consider */}
                  {strategist.why_consider.length > 0 && (
                    <BulletSection title="Why consider" items={strategist.why_consider} variant="success" />
                  )}

                  {/* Least bad plan */}
                  {strategist.least_bad_plan.length > 0 && (
                    <Box>
                      <Text fontSize="12px" fontWeight="500" lineHeight="18px" color="text.tertiary" mb="1">
                        Suggested approach
                      </Text>
                      <Box as="ol" m="0" pl="4" display="flex" flexDirection="column" gap="2px">
                        {strategist.least_bad_plan.map((step, i) => (
                          <Box as="li" key={i} fontSize="14px" color="text.secondary">{step}</Box>
                        ))}
                      </Box>
                    </Box>
                  )}

                  {/* Invalidate if */}
                  {strategist.invalidate_if.length > 0 && (
                    <BulletSection title="Stale if" items={strategist.invalidate_if} variant="error" />
                  )}

                  {/* Tone note */}
                  {strategist.tone_note && (
                    <Text fontSize="12px" fontWeight="500" lineHeight="18px" color="text.tertiary" fontStyle="italic" m="0">
                      {strategist.tone_note}
                    </Text>
                  )}
                </Flex>
              ) : null}
            </Box>
          )}

          {/* Error state */}
          {strategistError && !strategistLoading && !strategist && (
            <Box
              borderTop="1px dashed"
              borderColor="border.secondary"
              pt="3"
              mt="1"
            >
              <Text fontSize="12px" fontWeight="500" lineHeight="18px" color="text.tertiary" fontStyle="italic">
                AI commentary unavailable: {strategistError}
              </Text>
            </Box>
          )}
        </Flex>
      </CardBody>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function ScoreBar({
  label,
  score,
  maxScore,
  color,
  sublabel,
  authoritative: _authoritative,
}: {
  label: string;
  score: number;
  maxScore: number;
  color: string;
  sublabel?: string;
  authoritative?: boolean;
}) {
  const pct = Math.min(100, Math.max(0, (score / maxScore) * 100));

  return (
    <Box>
      <Flex justify="space-between" mb="4px">
        <Text fontSize="14px" color="text.secondary">
          {label}
          {sublabel && (
            <Text as="span" fontSize="12px" fontWeight="500" lineHeight="18px" color="text.tertiary" ml="2">
              {sublabel}
            </Text>
          )}
        </Text>
        <Text fontFamily="mono" fontSize="14px" css={{ fontVariantNumeric: 'tabular-nums' }}>{Math.round(score)}/{maxScore}</Text>
      </Flex>
      <Box
        h="8px"
        bg="bg.tertiary"
        borderRadius="full"
        overflow="hidden"
      >
        <Box
          h="full"
          w={`${pct}%`}
          borderRadius="full"
          bg={color}
          style={{ transition: 'width 300ms ease-out' }}
        />
      </Box>
    </Box>
  );
}

function BulletSection({
  title,
  items,
  variant: _variant,
}: {
  title: string;
  items: string[];
  variant: 'warning' | 'success' | 'error';
}) {
  return (
    <Box>
      <Text fontSize="12px" fontWeight="500" lineHeight="18px" color="text.tertiary" mb="1">
        {title}
      </Text>
      <Box as="ul" m="0" pl="4" display="flex" flexDirection="column" gap="2px">
        {items.map((item, i) => (
          <Box as="li" key={i} fontSize="14px" color="text.secondary">{item}</Box>
        ))}
      </Box>
    </Box>
  );
}

function riskBarColor(band: string | null): string {
  switch (band) {
    case 'low': return 'var(--chakra-colors-success-700, #027a48)';
    case 'moderate': return 'var(--chakra-colors-warning-500, #f79009)';
    case 'elevated': return 'var(--chakra-colors-warning-500, #f79009)';
    case 'high': return 'var(--chakra-colors-error-700, #b42318)';
    default: return 'var(--chakra-colors-gray-500, #667085)';
  }
}
